import type { ApprovalStatus, PublicApprovalOrder } from "../../../services/orderApprovalApi";

export type Lang = "en" | "hi";

const EN = {
  title: "Your order details",
  intro: "Please check these details. Tap Approve if everything is right.",
  orderCode: "Order",
  product: "Product",
  design: "Design",
  colour: "Colour",
  quantity: "Quantity",
  colours: "Colours",
  specs: "Details",
  neededBy: "Needed by",
  toBeConfirmed: "To be confirmed",
  deliverTo: "Delivery city",
  price: "Price",
  each: "each",
  total: "Total",
  advance: "Advance",
  balance: "Balance",
  photos: "Reference photos",
  policies: "Please note",
  checked: "I have checked these details",
  yourName: "Your name",
  approve: "Approve",
  approving: "Approving…",
  change: "Request a change",
  changeHelp: "Tell us what to change",
  sendChange: "Send",
  sending: "Sending…",
  cancel: "Back",
  thanks: "Thank you. Your order is approved.",
  thanksChange: "Thank you. We will get back to you shortly.",
  approvedOn: "Approved by {name} on {date}.",
  changeOn: "Change requested on {date}:",
  expired: "This link has expired. Please ask the studio for a new one.",
  revoked: "This link is no longer active.",
  superseded: "A newer version of these details was sent to you. Please open the latest link.",
  notFound: "This link isn't valid.",
  tooMany: "Too many tries. Please wait a minute and try again.",
  offline: "Couldn't load. Check your connection and try again.",
  retry: "Try again",
  nameNeeded: "Please enter your name",
  tickNeeded: "Please tick the box first",
  noteNeeded: "Please write what you would like changed",
  failed: "Something went wrong. Please try again.",
  serverError: "Something went wrong on our side. Please try again in a few minutes.",
  nameShort: "Please enter your full name (at least 2 letters)",
  noteTooLong: "Please keep it under 1000 characters",
  reload: "This page is out of date. Please reload it and try again.",
  loading: "Loading…",
  orderTable: "Order details",
  detail: "Detail",
  value: "Value",
  colourLines: "Colours and quantities",
  priceTable: "Price",
  amount: "Amount",
  unitPrice: "Price per piece",
  files: "Documents to check",
  fileName: "File",
  open: "Open",
  opening: "Opening…",
  fileFailed: "Couldn't open this file. Please try again.",
  fileImage: "Image",
  filePdf: "PDF",
  fileWord: "Word",
  fileOther: "File",
  advanceTitle: "Advance payment",
  advanceAmount: "Advance to pay",
  advanceDue: "Due",
  advanceSaysPaid: "You told us you've paid, waiting for the studio to confirm",
  advanceReceived: "Received",
  paidRef: "Your reference: {ref}",
  payDetails: "Payment details",
  upiId: "UPI ID",
  payee: "Payee",
  bank: "Bank",
  accountName: "Account name",
  accountNumber: "Account number",
  ifsc: "IFSC",
  payNote: "Note",
  copy: "Copy",
  copied: "Copied",
  payUpi: "Pay with UPI app",
  paidButton: "I've paid the advance",
  paidRefLabel: "Payment reference or UTR (optional)",
  paidSend: "Tell the studio",
  paidSending: "Sending…",
  noPayDetails: "The studio will share payment details with you.",
  consentTitle: "Updates about your saree (optional)",
  consentWhatsapp: "Send me updates about my saree on WhatsApp",
  consentEmail: "Send me updates about my saree by email",
  consentNone: "Please don't send me updates about my saree",
};

const HI: typeof EN = {
  title: "आपके ऑर्डर का विवरण",
  intro: "कृपया विवरण जाँच लें। सब सही हो तो ‘मंज़ूर करें’ दबाएँ।",
  orderCode: "ऑर्डर",
  product: "उत्पाद",
  design: "डिज़ाइन",
  colour: "रंग",
  quantity: "मात्रा",
  colours: "रंग",
  specs: "विवरण",
  neededBy: "कब तक चाहिए",
  toBeConfirmed: "अभी तय होना है",
  deliverTo: "डिलीवरी शहर",
  price: "कीमत",
  each: "प्रति नग",
  total: "कुल",
  advance: "एडवांस",
  balance: "बाकी",
  photos: "संदर्भ फ़ोटो",
  policies: "ध्यान दें",
  checked: "मैंने विवरण जाँच लिया है",
  yourName: "आपका नाम",
  approve: "मंज़ूर करें",
  approving: "मंज़ूर हो रहा है…",
  change: "बदलाव चाहिए",
  changeHelp: "बताइए क्या बदलना है",
  sendChange: "भेजें",
  sending: "भेजा जा रहा है…",
  cancel: "वापस",
  thanks: "धन्यवाद। आपका ऑर्डर मंज़ूर हो गया।",
  thanksChange: "धन्यवाद। हम जल्द ही आपसे संपर्क करेंगे।",
  approvedOn: "{name} ने {date} को मंज़ूर किया।",
  changeOn: "{date} को बदलाव माँगा गया:",
  expired: "यह लिंक समाप्त हो गया है। कृपया स्टूडियो से नया लिंक माँगें।",
  revoked: "यह लिंक अब चालू नहीं है।",
  superseded: "आपको इस विवरण का नया संस्करण भेजा गया है। कृपया नया लिंक खोलें।",
  notFound: "यह लिंक सही नहीं है।",
  tooMany: "बहुत बार कोशिश हुई। एक मिनट रुककर फिर कोशिश करें।",
  offline: "खुल नहीं पाया। इंटरनेट जाँचकर फिर कोशिश करें।",
  retry: "फिर कोशिश करें",
  nameNeeded: "कृपया अपना नाम लिखें",
  tickNeeded: "कृपया पहले बॉक्स पर टिक करें",
  noteNeeded: "कृपया लिखें कि क्या बदलना है",
  failed: "कुछ गड़बड़ हुई। कृपया फिर कोशिश करें।",
  serverError: "हमारी तरफ़ कुछ गड़बड़ हुई। कृपया कुछ मिनट बाद फिर कोशिश करें।",
  nameShort: "कृपया अपना पूरा नाम लिखें (कम से कम 2 अक्षर)",
  noteTooLong: "कृपया 1000 अक्षरों से कम में लिखें",
  reload: "यह पेज पुराना हो गया है। कृपया रीलोड करके फिर कोशिश करें।",
  loading: "लोड हो रहा है…",
  orderTable: "ऑर्डर का विवरण",
  detail: "विवरण",
  value: "जानकारी",
  colourLines: "रंग और मात्रा",
  priceTable: "कीमत",
  amount: "राशि",
  unitPrice: "प्रति नग कीमत",
  files: "जाँचने के लिए दस्तावेज़",
  fileName: "फ़ाइल",
  open: "खोलें",
  opening: "खुल रहा है…",
  fileFailed: "यह फ़ाइल नहीं खुली। कृपया फिर कोशिश करें।",
  fileImage: "फ़ोटो",
  filePdf: "PDF",
  fileWord: "Word",
  fileOther: "फ़ाइल",
  advanceTitle: "एडवांस भुगतान",
  advanceAmount: "देय एडवांस",
  advanceDue: "बाकी है",
  advanceSaysPaid: "आपने बताया कि भुगतान हो गया है, स्टूडियो की पुष्टि का इंतज़ार है",
  advanceReceived: "मिल गया",
  paidRef: "आपका संदर्भ: {ref}",
  payDetails: "भुगतान का विवरण",
  upiId: "UPI ID",
  payee: "पाने वाले",
  bank: "बैंक",
  accountName: "खाते का नाम",
  accountNumber: "खाता संख्या",
  ifsc: "IFSC",
  payNote: "नोट",
  copy: "कॉपी करें",
  copied: "कॉपी हो गया",
  payUpi: "UPI ऐप से भुगतान करें",
  paidButton: "मैंने एडवांस भेज दिया है",
  paidRefLabel: "भुगतान संदर्भ या UTR (वैकल्पिक)",
  paidSend: "स्टूडियो को बताएँ",
  paidSending: "भेजा जा रहा है…",
  noPayDetails: "स्टूडियो आपको भुगतान का विवरण भेजेगा।",
  consentTitle: "आपकी साड़ी के अपडेट (वैकल्पिक)",
  consentWhatsapp: "मेरी साड़ी के अपडेट मुझे WhatsApp पर भेजें",
  consentEmail: "मेरी साड़ी के अपडेट मुझे ईमेल पर भेजें",
  consentNone: "कृपया मेरी साड़ी के अपडेट न भेजें",
};

export type TextKey = keyof typeof EN;

export const text = (lang: Lang, key: TextKey, vars: Record<string, string> = {}): string =>
  Object.entries(vars).reduce((out, [k, v]) => out.split(`{${k}}`).join(v), (lang === "hi" ? HI : EN)[key]);

/** "Priya Sharma Ma'am" when the studio gave a title, else the name alone. */
export const greetingName = (order?: PublicApprovalOrder): string => {
  const name = (order?.customerName || "").trim();
  const title = (order?.customerTitle || "").trim();
  return name && title ? `${name} ${title}` : name;
};

/** Indian grouping for rupees (₹1,25,000); other currencies keep their code. */
export const money = (value?: string | null, currency = "INR"): string => {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const formatted = n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return currency === "INR" ? `₹${formatted}` : `${formatted} ${currency}`;
};

/** 1536000 → "1.5 MB"; unknown size gives "". */
export const fileSize = (bytes?: number | null): string => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
};

/** The file's kind in plain words, from its content type or name. */
export const fileKind = (lang: Lang, contentType?: string | null, name?: string | null): string => {
  const type = (contentType || "").toLowerCase();
  const ext = (name || "").toLowerCase().split(".").pop() || "";
  if (type.startsWith("image/") || ["jpg", "jpeg", "png", "webp", "heic"].includes(ext)) return text(lang, "fileImage");
  if (type === "application/pdf" || ext === "pdf") return text(lang, "filePdf");
  if (type.includes("word") || ext === "doc" || ext === "docx") return text(lang, "fileWord");
  return text(lang, "fileOther");
};

/** 2027-01-15 → "15 Jan 2027" (or Hindi month names). Bad input returns as is. */
export const day = (iso?: string | null, lang: Lang = "en"): string => {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00+05:30` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

/** The server's known 400 answers, in the customer's language; anything else gets the generic line. */
const SERVER_MESSAGES: Array<[RegExp, TextKey]> = [
  [/^Please enter your name/, "nameNeeded"],
  [/^Please tick/, "tickNeeded"],
  [/^Please write what you would like changed/, "noteNeeded"],
  [/^Please keep it under/, "noteTooLong"],
];

export const serverMessage = (lang: Lang, message: string): string => {
  const hit = SERVER_MESSAGES.find(([pattern]) => pattern.test(message || ""));
  return text(lang, hit ? hit[1] : "failed");
};

export const endedMessage = (state: ApprovalStatus, lang: Lang): string | null => {
  if (state === "EXPIRED") return text(lang, "expired");
  if (state === "REVOKED") return text(lang, "revoked");
  if (state === "SUPERSEDED") return text(lang, "superseded");
  return null;
};

const LANG_KEY = "hkp.approval.lang";

export const savedLang = (fallback: Lang): Lang => {
  try {
    const v = window.localStorage.getItem(LANG_KEY);
    return v === "hi" || v === "en" ? v : fallback;
  } catch {
    return fallback;
  }
};

export const saveLang = (lang: Lang) => {
  try {
    window.localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Private mode: the toggle still works for this visit.
  }
};
